import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useTrendingTopics } from 'src/api/queries';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Skeleton } from 'src/components/ui/Skeleton';
import { formatDate } from 'src/lib/time';
import { useDebouncedValue } from 'src/lib/useDebouncedValue';
import type { EditorialTopicWithTrend } from 'src/types/editorial';

/**
 * Answers: what has this newsroom been covering, and which of it is moving?
 *
 * The first screen in the product that is not scoped to one broadcast. Everything else here
 * drills into a stream; a topic exists precisely because it spans them, so the row's unit is the
 * subject and the broadcast is a detail underneath it.
 */

/** Matches the pipeline's PRIMARY_CATEGORIES. Spanish labels because the desk reads Spanish. */
const CATEGORY_LABEL: Record<string, string> = {
  politics_government: 'Política y gobierno',
  economy_business: 'Economía',
  security_justice: 'Seguridad y justicia',
  health: 'Salud',
  education: 'Educación',
  environment: 'Medio ambiente',
  infrastructure_mobility: 'Infraestructura',
  science_technology: 'Ciencia y tecnología',
  society_human_rights: 'Sociedad y derechos',
  international: 'Internacional',
  sports: 'Deportes',
  culture_entertainment: 'Cultura',
  other: 'Otros',
};

export function TrendsWorkspace() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [onlyRecurring, setOnlyRecurring] = useState(false);
  const [page, setPage] = useState(1);
  // Only the typed text waits. The select and the toggle are decisions already made.
  const debouncedSearch = useDebouncedValue(search.trim());
  const topics = useTrendingTopics({
    search: debouncedSearch,
    category,
    minMentions: onlyRecurring ? 2 : undefined,
    page,
  });
  // True while the typed text is ahead of the results on screen. Distinct from `isPending`, which
  // now only happens on the very first load: after that the previous page is held in place.
  const settling = search.trim() !== debouncedSearch || topics.isFetching;

  /** Any filter change invalidates the page number — page 4 of a different result set is not a
   *  page the reader asked for, and an empty screen after typing reads as "no results". */
  function refine(apply: () => void) {
    apply();
    setPage(1);
  }

  return (
    <div className="page-wide">
      <PageHeader title="Tendencias">
        Los asuntos que el noticiero ha cubierto, no las transmisiones donde aparecieron. Un tema
        junta muchas historias de muchos días: aquí se ve cuáles siguen creciendo, cuáles llevaban
        semanas callados y volvieron, y en qué canales se contaron.
      </PageHeader>

      <form className="topics-filters" onSubmit={event => event.preventDefault()} role="search">
        <label className="topics-filters__field">
          <span>Buscar</span>
          <input
            type="search"
            value={search}
              onChange={event => refine(() => setSearch(event.target.value))}
            placeholder="reforma judicial, huachicol…"
          />
        </label>

        <label className="topics-filters__field">
          <span>Categoría</span>
          <select value={category} onChange={event => refine(() => setCategory(event.target.value))}>
            <option value="">Todas</option>
            {Object.entries(CATEGORY_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        {/* A topic mentioned once is a story, not yet a subject. This is the fastest way to see
            only the things that actually recur, which is what the screen is for. */}
        <Button type="button" variant={onlyRecurring ? 'primary' : 'ghost'} onClick={() => refine(() => setOnlyRecurring(v => !v))}>
          Solo recurrentes
        </Button>
      </form>

      {topics.isPending && <Skeleton variant="rows" count={6} />}
      {topics.isError && <LoadFailure title="No se pudieron cargar los temas" error={topics.error} onRetry={topics.refetch} />}

      {topics.data && topics.data.topics.length === 0 && (
        <p className="topics__empty">
          {search || category || onlyRecurring
            ? 'Ningún tema coincide con ese filtro.'
            : 'Todavía no hay temas. Se crean cuando el pipeline enlaza las historias de una transmisión.'}
        </p>
      )}

      {topics.data && topics.data.topics.length > 0 && (
        <>
          <ol className="topics" data-settling={settling ? 'true' : undefined} aria-busy={settling}>
            {topics.data.topics.map(topic => (
              <TopicRow key={topic.topicId} topic={topic} />
            ))}
          </ol>
          <nav className="topics__pager" aria-label="Paginación">
            <Button type="button" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => Math.max(p - 1, 1))}>
              ← Anterior
            </Button>
            <span className="topics__count">
              {topics.data.totalRecords} tema{topics.data.totalRecords === 1 ? '' : 's'}
              {topics.data.totalPages > 1 && ` · página ${page} de ${topics.data.totalPages}`}
            </span>
            <Button type="button" variant="ghost" disabled={!topics.data.hasNextPage} onClick={() => setPage(p => p + 1)}>
              Siguiente →
            </Button>
          </nav>
        </>
      )}
    </div>
  );
}

function TopicRow({ topic }: { topic: EditorialTopicWithTrend }) {
  return (
    <li className="topic-row">
      <div className="topic-row__who">
        <Link className="topic-row__name" to={`/tendencias/${encodeURIComponent(topic.topicId)}`}>
          {topic.canonicalName || 'Tema sin título'}
        </Link>
        <span className="topic-row__category">{CATEGORY_LABEL[topic.category] ?? topic.category}</span>
      </div>

      <div className="topic-row__signals">
        <span className="topic-row__stat">
          {topic.mentionCount} mención{topic.mentionCount === 1 ? '' : 'es'}
        </span>
        {/* Heat is what actually orders these rows; a list sorted by an invisible number reads as
            arbitrary. Shown, the order explains itself. */}
        {topic.trend.heat > 0 && (
          <span className="topic-row__stat" title="Menciones ponderadas por recencia (vida media: 14 días). Ordena esta lista.">
            calor {topic.trend.heat.toFixed(1)}
          </span>
        )}
        {/* Momentum over one mention is arithmetic, not a reading: every new topic would
            claim it is rising. A subject needs a second mention before it has a direction. */}
        {topic.mentionCount > 1 && <Momentum value={topic.trend.momentum} />}
        {/* Absence is a state: a topic that went quiet and came back is the single most useful
            thing this screen can point at, so it gets a mark rather than a number. */}
        {topic.trend.resurfaced && <Badge tone="unresolved">Reapareció</Badge>}
      </div>

      <div className="topic-row__when">
        {topic.firstSeenAt && topic.lastSeenAt && (
          <span className="topic-row__span">
            {formatDate(topic.firstSeenAt)} — {formatDate(topic.lastSeenAt)}
          </span>
        )}
      </div>
    </li>
  );
}

/** A slope, not a forecast: mentions in the last fortnight against the fortnight before it. */
function Momentum({ value }: { value: number }) {
  if (value === 0) return <span className="topic-row__stat topic-row__stat--flat">sin cambio</span>;
  const rising = value > 0;
  return (
    <span className={`topic-row__stat ${rising ? 'topic-row__stat--up' : 'topic-row__stat--down'}`}>
      {rising ? '↑' : '↓'} {Math.abs(value)} vs. quincena anterior
    </span>
  );
}
